import React, { useState, useEffect } from 'react'
import { SidebarData } from './SidebarData'
import './Sidebar.css'
import { useNavigate, useLocation } from 'react-router-dom'
import Cookies from 'js-cookie'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { checkPermissionKey, updatePermissionCache } from '../../utils/auth'

const API_BASE = 'https://system.sales-management.cloud/api/permissions.php';

function Sidebar({ isOpen, toggleSidebar }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [userInfo, setUserInfo] = useState(null);
    const [expandedMenu, setExpandedMenu] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [userPermissions, setUserPermissions] = useState(new Set());
    // eslint-disable-next-line no-unused-vars
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);

    useEffect(() => {
        // Cookieからユーザー情報を取得する関数
        const loadUserInfo = () => {
            const userInfoCookie = Cookies.get('user_info');
            if (userInfoCookie) {
                try {
                    const parsedUserInfo = JSON.parse(userInfoCookie);
                    setUserInfo(parsedUserInfo);
                    // ユーザーが変更されたら、権限を再読み込み
                    loadSidebarPermissions(parsedUserInfo.id);
                } catch (error) {
                    console.error('Failed to parse user info:', error);
                }
            } else {
                setUserInfo(null);
            }
        };

        // サイドバー権限を取得
        const loadSidebarPermissions = async (userId) => {
            try {
                const token = Cookies.get('auth_token');
                if (!token) {
                    setPermissionsLoaded(true);
                    return;
                }
                
                const response = await fetch(
                    `${API_BASE}?action=get_user_permissions&user_id=${userId}&include_role_permissions=1`,
                    {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
                
                const data = await response.json();
                if (data.status === 'success') {
                    const permissionKeys = (data.data.permissions || [])
                        .map((permission) => permission.permission_key)
                        .filter(Boolean);
                    setUserPermissions(new Set(permissionKeys));
                    updatePermissionCache(permissionKeys);
                } else {
                    console.warn('Failed to load sidebar permissions:', data.message);
                    // 権限が取得できない場合はロール既定表示にフォールバック
                    setUserPermissions(new Set());
                    updatePermissionCache([]);
                }
            } catch (error) {
                console.error('Error loading sidebar permissions:', error);
                setUserPermissions(new Set());
                updatePermissionCache([]);
            } finally {
                setPermissionsLoaded(true);
            }
        };

        // 初回読み込み
        loadUserInfo();

        // ストレージイベントをリッスン（他のタブでの変更を検知）
        const handleStorageChange = (e) => {
            if (e.key === 'user_info_updated') {
                loadUserInfo();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // 定期的にCookieを再確認（3000ms間隔）
        const interval = setInterval(loadUserInfo, 3000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    // メニュー項目が表示可能かチェック
    const canShowMenu = (menuItem) => {
        // superadmin は無条件に全て表示
        if (userInfo && userInfo.role === 'superadmin') {
            return true;
        }
        
        // permissionKey がない場合は常に表示
        if (!menuItem.permissionKey) {
            return true;
        }
        
        // permissionKey による権限チェック
        return checkPermissionKey(menuItem.permissionKey);
    };

    // 管理者権限チェック（後方互換性のため残す）
    const isAdmin = userInfo && (userInfo.role === 'admin' || userInfo.role === 'view_admin');
    // 契約者チェック（後方互換性のため残す）
    const isContractor = userInfo && userInfo.role === 'contractor';

    return (
        <div className={`Sidebar ${isOpen ? 'open' : ''}`}>
            <div className='SidebarScrollContainer'>
                <ul className='SidebarList'>
                    {SidebarData.map((item, index) => {
                        // サイドバー権限チェック（新しいシステム）
                        if (!canShowMenu(item)) return null;
                        
                        // 後方互換性のため、adminOnly / contractorOnly もチェック
                        if (item.adminOnly && !isAdmin) return null;
                        if (item.contractorOnly && !isContractor) return null;

                        const isExpanded = expandedMenu === item.title;
                        // サブメニューはフィルタ済みのものを使う
                        const filteredSubmenu = (item.submenu || []).filter(si => {
                            // サイドバー権限チェック（新しいシステム）
                            if (!canShowMenu(si)) return false;
                            
                            // 後方互換性のため、adminOnly / contractorOnly もチェック
                            if (si.adminOnly && !isAdmin) return false;
                            if (si.contractorOnly && !isContractor) return false;
                            return true;
                        });
                        const hasSubmenu = filteredSubmenu.length > 0;

                        return (
                            <div key={index}>
                                <li
                                    id={location.pathname === item.link ? 'active' : ''}
                                    className='row'
                                    onClick={() => {
                                        if (hasSubmenu) {
                                            setExpandedMenu(isExpanded ? null : item.title);
                                        } else {
                                            navigate(item.link);
                                            if (window.innerWidth < 768) {
                                                toggleSidebar();
                                            }
                                        }
                                    }}
                                >
                                    <div id='icon'>{item.icon}</div>
                                    <div id='title'>{item.title}</div>
                                    {hasSubmenu && (
                                        <div className='expand-icon' style={{ marginLeft: 'auto' }}>
                                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </div>
                                    )}
                                </li>

                                {/* サブメニュー */}
                                {hasSubmenu && isExpanded && (
                                    <ul className='SidebarSubmenu'>
                                        {filteredSubmenu.map((submenuItem, subIndex) => (
                                            <li
                                                key={subIndex}
                                                id={location.pathname === submenuItem.link ? 'active' : ''}
                                                className='submenu-row'
                                                onClick={() => {
                                                    navigate(submenuItem.link);
                                                    if (window.innerWidth < 768) {
                                                        toggleSidebar();
                                                    }
                                                }}
                                            >
                                                <div id='submenu-icon'>{submenuItem.icon}</div>
                                                <div id='submenu-title'>{submenuItem.title}</div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )
                    })}
                </ul>
            </div>
            
            {/* ユーザー情報表示エリア */}
            {userInfo && (
                <div className='UserInfo' onClick={() => navigate('/login')}>
                    <div className='UserInfoContent'>
                        <AccountCircleIcon className='UserIcon' />
                        <div className='UserDetails'>
                            <div className='UserName'>{userInfo.name}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Sidebar